import { ERROR_MESSAGES } from 'common/strings';
import { isEmpty as isEmptyLodash } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import { GROUP_RULE_TYPE } from 'ui/containers/BuildConversation/options';
import trimInsentJsonEnds from 'utils/trimInsentJsonEnds';
import isEmpty from 'validator/lib/isEmpty';
import isMobilePhone from 'validator/lib/isMobilePhone';
import isURL from 'validator/lib/isURL';
import omit from 'lodash/omit';
import DOMPurify from 'dompurify';

const MAX_INTENTS_TOPICS = 50;

export const validateRange = val => {
  return parseInt(val) > -1 ? undefined : 'Required';
};

export const numbersArrayValidator = (value, conEle) => {
  if (isEmptyLodash(value)) {
    return ERROR_MESSAGES.FIELD_EMPTY;
  } else {
    if (conEle.key === GROUP_RULE_TYPE.INTENTS) {
      const sumOfTopics = value.reduce((acc, curr) => acc + curr?.products?.length, 0);
      if (sumOfTopics > MAX_INTENTS_TOPICS) {
        return `Maximum ${MAX_INTENTS_TOPICS} topics allowed`;
      }
    }
    return undefined;
  }
};

export const checkEmpty = value => {
  let error;
  if (value === undefined || isEmpty(String(value))) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  }
  return error;
};
export const checkEmptyObject = value => {
  let error;
  if (value === undefined || isEmpty(Object(value))) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  }
  return error;
};

export const checkMaxCharacters = (value, maxCharacters) => {
  let error;
  if (value === undefined || isEmpty(String(value))) {
    error = 'This field cannot be empty';
  } else if (value.length > maxCharacters) {
    error = 'Maximum of ' + maxCharacters + ' characters are allowed';
  }
  return error;
};

export const checkConditionValue = value => {
  let error;
  if (isEmptyLodash(value)) {
    if (!Number.isInteger(value)) {
      error = `Field cannot be empty`;
    }
  } else if (typeof value === 'string') {
    if (checkEmptyString(value)) {
      error = `Field cannot be empty`;
    }
  }
  return error;
};

export const checkEmptyString = value => {
  let error;
  const clean = DOMPurify.sanitize(value, { ALLOWED_TAGS: [] });
  if (value === undefined || isEmpty(String(value)) || isEmpty(String(value).trim())) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  } else if (clean !== value) {
    error = `Please enter valid value`;
  }
  return error;
};

export const checkPositiveInteger = value => {
  let error;
  if (!Number.isInteger(value) || value <= 0) {
    error = 'Value must be a positive integer';
  }
  return error;
};

export const checkNumber = value => {
  let error;
  const regexp = /^\d+(\.\d{1,2})?$/;
  if (value === undefined || isEmpty(String(value)) || Number.isNaN(value)) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  } else if (value < 0) {
    error = 'Value must be a positive number';
  } else if (!regexp.test(value)) {
    error = 'Value can be upto 2 decimals only';
  }
  return error;
};

export const checkBasicNumber = value => {
  let error;

  const regex = /^-?(0|[1-9]\d*)(\.\d+)?$/; //Allows any number that can have positive ,negative and decimals
  if (value === undefined || isEmpty(String(value))) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  } else if (!regex.test(value)) {
    error = 'This field must be a number';
  }
  return error;
};

export const checkEmptyArrayRegular = value => {
  let error;
  try {
    if (value.length <= 0) {
      error = ERROR_MESSAGES.FIELD_EMPTY;
    }
  } catch (e) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  }
  return error;
};

export const checkEmptyArray = value => {
  let error;
  try {
    if (value.content.length <= 0) {
      error = ERROR_MESSAGES.FIELD_EMPTY;
    }
  } catch (e) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  }
  return error;
};

export const checkInputFieldName = value => {
  let error;
  let regexp = /^[a-zA-Z0-9-_]+$/;
  if (value === undefined || isEmpty(String(value))) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  } else if (value.search(regexp) === -1) {
    error = 'Use any of characters a-z, A-Z, 0-9, _ , - ';
  }
  return error;
};

export const checkBuilderInputFieldName = value => {
  let error;
  let regexp = /^[a-zA-Z0-9-_]+$/;
  if (value === undefined || isEmpty(String(value))) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  } else if (value.search(regexp) === -1) {
    error = 'Use any of characters a-z, A-Z, 0-9, _ , - ';
  } else if (value.toLowerCase().startsWith('insentdynamic')) {
    error = 'This suffix is not allowed to use';
  }
  return error;
};

export const checkUrl = value => {
  let error;
  if (value === undefined || isEmpty(String(value))) {
    error = ERROR_MESSAGES.FIELD_EMPTY;
  } else if (!isURL(value, { protocols: ['http', 'https'] })) {
    error = 'Please provide a valid url';
  }

  return error;
};

export const checkPhoneNumber = value => {
  let error;
  if (isEmpty(String(value))) {
    return error;
  }

  if (!isMobilePhone(value)) {
    error = 'Phone number is not valid';
  }
  return error;
};

export const validateUrl = value => {
  let error;
  if (isEmpty(String(value))) {
    return error;
  }
  if (!isURL(value, { protocols: ['http', 'https'] })) {
    error = 'Please provide a valid url';
  }
  return error;
};

export const checkEmail = email => {
  let error;
  const validFormat = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/gim;
  if (!validFormat.test(email)) {
    error = 'Please enter a valid email address!';
  }
  return error;
};

export const validateParticipants = value => {
  if (value) {
    const { teams, members } = value || {};
    if (teams || members) {
      return false;
    } else {
      return ERROR_MESSAGES.FIELD_EMPTY;
    }
  }
};

export const durationInDaysValidation = duration => {
  let error;
  if (typeof duration === 'undefined' || !Number.isInteger(duration)) {
    error = 'Duration cannot be empty';
  } else if (duration < 1) {
    error = 'Please enter a positive duration';
  } else if (duration > 999) {
    error = 'Please enter a duration less than 999';
  }
  return error;
};

function stripMentionAttrsFromEditorJSON(contents) {
  if (!contents || contents.length <= 0) {
    return;
  }
  contents.forEach(paragraphContent => {
    if (paragraphContent.type === 'paragraph') {
      paragraphContent.content.forEach((content, index) => {
        if (content.type === 'mention') {
          const { attrs } = content;
          const { id, ...rest } = attrs || {};
          if (id) {
            content.attrs = {
              ...rest,
            };
          }
        }
      });
    } else if (paragraphContent.type === 'bulletList' || paragraphContent.type === 'orderedList') {
      if (paragraphContent.content && paragraphContent.content.length > 0) {
        paragraphContent.content.forEach(listContent => {
          if (listContent.type === 'listItem') {
            stripMentionAttrsFromEditorJSON(listContent.content);
          }
        });
      }
    }
  });
  return contents;
}

export const validateVariantsEquality = variants => {
  let error;
  if (Array.isArray(variants) && variants.length > 0) {
    const variantValues = variants.map(v => {
      const variantJson = cloneDeep(v);
      const {
        value: { content },
        gif,
      } = variantJson || { value: {} };
      const mentionStrippedJson = content && stripMentionAttrsFromEditorJSON(content);
      if (mentionStrippedJson) variantJson.value.content = mentionStrippedJson;
      return {
        ...trimInsentJsonEnds(variantJson.value),
        ...{
          ...(gif && {
            gif: gif.map(gif =>
              omit(gif, ['time', 'name', 'original', 'previewGif', 'previewStill'])
            ),
          }),
        },
      };
    });
    const uniqVariants = uniqWith(variantValues, isEqual);
    if (!uniqVariants || uniqVariants.length !== variants.length) {
      error = 'Duplicate greeting messages found';
    }
  } else {
    error = 'Add atleast one variant';
  }

  return error;
};

export const allowOnlyPositiveInteger = evt => {
  const ASCIICode = evt.which ? evt.which : evt.keyCode;
  if (ASCIICode > 31 && (ASCIICode < 48 || ASCIICode > 57)) {
    evt.preventDefault();
  }
};

export const allowOnlyPositiveFloatNumbers = evt => {
  if (evt.key === '.') {
    return false;
  } else {
    const ASCIICode = evt.which ? evt.which : evt.keyCode;
    if (ASCIICode > 31 && (ASCIICode < 48 || ASCIICode > 57)) {
      evt.preventDefault();
    }
  }
};
